import { QueryClient } from '@tanstack/react-query';

/**
 * React Query client configured for Matrix Tank.
 *
 * Default settings:
 * - 30 second stale time (data considered fresh)
 * - 5 minute garbage collection time
 * - 2 retries on failure
 * - Refetch on window focus disabled (manual control)
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000, // 30 seconds
      gcTime: 5 * 60 * 1000, // 5 minutes (formerly cacheTime)
      retry: 2,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
    },
    mutations: {
      retry: 1,
    },
  },
});

/**
 * Query key factories for consistent cache key management.
 *
 * Usage:
 * - queryKeys.photos.all - All photos queries
 * - queryKeys.photos.list(filters) - Specific photo list
 * - queryKeys.photos.detail(id) - Single photo
 */
export const queryKeys = {
  // Photos
  photos: {
    all: ['photos'],
    lists: () => [...queryKeys.photos.all, 'list'],
    list: (filters) => [...queryKeys.photos.lists(), filters],
    details: () => [...queryKeys.photos.all, 'detail'],
    detail: (id) => [...queryKeys.photos.details(), id],
    stats: () => [...queryKeys.photos.all, 'stats'],
  },

  // Faces
  faces: {
    all: ['faces'],
    lists: () => [...queryKeys.faces.all, 'list'],
    list: (filters) => [...queryKeys.faces.lists(), filters],
    byPhoto: (photoId) => [...queryKeys.faces.all, 'byPhoto', photoId],
    neighbors: (faceId) => [...queryKeys.faces.all, 'neighbors', faceId],
  },

  // Persons
  persons: {
    all: ['persons'],
    lists: () => [...queryKeys.persons.all, 'list'],
    list: (filters) => [...queryKeys.persons.lists(), filters],
    detail: (id) => [...queryKeys.persons.all, 'detail', id],
    search: (query) => [...queryKeys.persons.all, 'search', query],
  },

  // Teams
  teams: {
    all: ['teams'],
    list: () => [...queryKeys.teams.all, 'list'],
    detail: (id) => [...queryKeys.teams.all, 'detail', id],
  },

  // Matches
  matches: {
    all: ['matches'],
    list: (filters) => [...queryKeys.matches.all, 'list', filters],
    detail: (id) => [...queryKeys.matches.all, 'detail', id],
  },

  // Profiles
  profiles: {
    all: ['profiles'],
    list: () => [...queryKeys.profiles.all, 'list'],
    detail: (id) => [...queryKeys.profiles.all, 'detail', id],
  },

  // Auth
  auth: {
    user: ['auth', 'user'],
    status: ['auth', 'status'],
  },
};
